import LogoIconOnly from "@medoma/components/icons/LogoIconOnly";
import { Link, useNavigate } from "react-router";
import { SiteSwitcher } from "@/components/SiteSwitcher/SiteSwitcher";
import { IntercomButton } from "@medoma/support/IntercomButton";
import styles from "./TemplateManager.module.scss";
import TemplatesList from "./TemplatesList";
import { Outlet } from "react-router";
import { Heading } from "@medoma/components/Heading/Heading";
import { Trans, useLingui } from "@lingui/react/macro";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import { startOfToday } from "date-fns";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import BulletListIcon from "@medoma/components/icons/BulletListIcon";
import FolderIcon from "@medoma/components/icons/FolderIcon";
import { CompetenceFilter } from "./CompetenceFilter";
import { useTemplateManagerCompetenceFilter } from "./useTemplateManagerCompetenceFilter";
import { usePatientTypes } from "@/api/Patients";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";

const TemplateManager = () => {
  return (
    <div className={styles.templateManagerContainer}>
      <header className={styles.header}>
        <nav className={styles.nav}>
          <Link to="/" className={styles.logo}>
            <LogoIconOnly />
          </Link>
          <SiteSwitcher />
        </nav>
        <IntercomButton />
      </header>
      <Outlet />
    </div>
  );
};

const TemplatesPage = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const {
    data: patientTypes,
    isPending: isPendingPatientTypes,
    isError: isErrorPatientTypes,
    error: errorPatientTypes,
  } = usePatientTypes();
  const { filteredCompetence, selectCompetence, matchesCompetenceFilter } =
    useTemplateManagerCompetenceFilter();

  if (isPendingPatientTypes) {
    return <Loading message={t`Hämtar patienttyper`} />;
  }

  if (isErrorPatientTypes) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patienttyper.`} ${deducedError(errorPatientTypes)}`}
      />
    );
  }

  if (patientTypes.length === 0) {
    return (
      <ErrorMessage
        message={t`Inga patienttyper hittades. Kontakta support.`}
      />
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.left}>
        <div className={styles.mainHeader}>
          <Heading level="h1">
            <Trans>Mallar</Trans>
          </Heading>
          <div className={styles.actions}>
            <OutlinedButton size="small" onClick={() => navigate("groups/new")}>
              <FolderIcon />
              <Trans>Ny grupp</Trans>
            </OutlinedButton>
            <FilledButton
              size="small"
              onClick={() => navigate("activity-templates/new")}
            >
              <PlusIcon />
              <Trans>Ny aktivitet</Trans>
            </FilledButton>
            <FilledButton
              size="small"
              onClick={() => navigate("batch-templates/new")}
            >
              <BulletListIcon />
              <Trans>Nytt paket</Trans>
            </FilledButton>
          </div>
        </div>
        <CompetenceFilter
          filteredCompetence={filteredCompetence}
          selectCompetence={selectCompetence}
        />
        <TemplatesList
          patientTypes={patientTypes}
          matchesCompetenceFilter={matchesCompetenceFilter}
        />
      </div>
      <aside className={styles.right}>
        <Outlet
          context={{
            patientId: undefined,
            defaultStartDate: startOfToday(),
            patientTypes,
          }}
        />
      </aside>
    </main>
  );
};

export { TemplateManager, TemplatesPage };
